/**
 * depthBias — the depth convention the renderer runs, and two three.js r185.1 polygon-offset bugs, backported
 * (shader gaps R5, 2026-09-29 — the reversed-Z study; `verify/depth-bed-probe.mjs` in Atlas pictures both).
 *
 * 1. THE SHARED PIPELINE (standard depth too). r185.1's `WebGPUBackend.getRenderCacheKey` leaves `polygonOffset` /
 *    `polygonOffsetFactor` / `polygonOffsetUnits` out of the render-pipeline key, and the material key reduces numbers
 *    to on/off — so a decal whose shader code matches any earlier non-offset material REUSES that material's pipeline
 *    and draws with no depth bias at all (the bed: offsets of 0, ±2, ±64 and ±4,096 all drew the same). Atlas's road
 *    ribbons (plain `MeshStandardMaterial`s) are exactly that case. three `dev` (r187) adds the three fields to the key;
 *    the backport appends them.
 * 2. THE REVERSED SIGN. Under reversed depth a larger depth is NEARER, so a "toward the camera" offset (negative, the
 *    authoring convention) must be negated; r185.1 hands the material's literal to the backend unchanged on both
 *    backends, and every decal sinks into its surface. three r187 (PR #34606) negates units and factor in
 *    `WebGPUPipelineUtils` and `WebGLState`; the backport negates them in the render-pipeline descriptor. The engine
 *    only enables reversed depth on WebGPU, so the WebGPU half is the one needed.
 *
 * Both backports check three's REVISION and stand down at r187+ (where three does it itself — a second negation
 * would sink every decal again). Materials keep writing the standard literals (−1, −2, −4).
 */
import * as THREE from 'three/webgpu';

let reversed = false;

/** Called by EngineRenderer.create after init with what the renderer really runs. */
export function setDepthConvention(isReversed: boolean): void {
  reversed = isReversed;
}

/** True when the renderer runs reversed depth (1 at the near plane, 0 at the far plane). */
export function reversedDepthActive(): boolean {
  return reversed;
}

/** three fixed both bugs in r187 (the cache key on `dev`, the reversed sign in PR #34606). */
export const THREE_FIXES_POLYGON_OFFSET = Number.parseInt(String((THREE as unknown as { REVISION?: string }).REVISION ?? '0'), 10) >= 187;

/** The render-pipeline key suffix r185.1 is missing (pure — pinned by the engine test). */
export function polygonOffsetKeySuffix(material: { polygonOffset?: boolean; polygonOffsetFactor?: number; polygonOffsetUnits?: number } | null | undefined): string {
  return material?.polygonOffset === true ? `,po${material.polygonOffsetFactor ?? 0},${material.polygonOffsetUnits ?? 0}` : '';
}

/** Negate a render-pipeline descriptor's depth bias in place (reversed depth) — pure, pinned by the engine test. */
export function flipDepthBias(descriptor: { depthStencil?: { depthBias?: number; depthBiasSlopeScale?: number } } | null | undefined): void {
  const ds = descriptor?.depthStencil;
  if (!ds) return;
  if (typeof ds.depthBias === 'number' && ds.depthBias !== 0) ds.depthBias = -ds.depthBias;
  if (typeof ds.depthBiasSlopeScale === 'number' && ds.depthBiasSlopeScale !== 0) ds.depthBiasSlopeScale = -ds.depthBiasSlopeScale;
}

/**
 * Install the two backports on a WebGPU renderer (a no-op on the WebGL2 backend, and at three r187+). Returns what it
 * installed, for the log and the tests.
 */
export function installPolygonOffsetBackports(renderer: unknown): { cacheKey: boolean; reversedSign: boolean } {
  const out = { cacheKey: false, reversedSign: false };
  if (THREE_FIXES_POLYGON_OFFSET) return out;
  const backend = (renderer as { backend?: {
    isWebGPUBackend?: boolean;
    getRenderCacheKey?: (ro: { material?: unknown }) => string;
    device?: { createRenderPipeline?: (d: unknown) => unknown; createRenderPipelineAsync?: (d: unknown) => Promise<unknown> };
    __enginexPolyBackport?: boolean;
  } } | null)?.backend;
  if (!backend || backend.isWebGPUBackend !== true || backend.__enginexPolyBackport) return out;
  backend.__enginexPolyBackport = true;
  const origKey = backend.getRenderCacheKey?.bind(backend);
  if (origKey) {
    backend.getRenderCacheKey = (ro) => origKey(ro) + polygonOffsetKeySuffix(ro?.material as never);
    out.cacheKey = true;
  }
  const isReversed = (renderer as { reversedDepthBuffer?: boolean }).reversedDepthBuffer === true;
  const device = backend.device;
  if (isReversed && device) {
    const sync = device.createRenderPipeline?.bind(device);
    const async = device.createRenderPipelineAsync?.bind(device);
    if (sync) device.createRenderPipeline = (d: unknown) => { flipDepthBias(d as never); return sync(d); };
    if (async) device.createRenderPipelineAsync = (d: unknown) => { flipDepthBias(d as never); return async(d); };
    out.reversedSign = !!(sync || async);
  }
  return out;
}
